import CatalogEntity from "../Components/CatalogEntity";
import {useEffect, useState} from "react";
import {ArrayTextFieldEditor, SelectFieldCellEditor, renderColumnValue, findEnumLabelByValue} from "../../../General/SharedMethods";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, TextField} from "@mui/material";
import {MRT_EditActionButtons} from "material-react-table";
import * as yup from 'yup';

const PredefinedInstallationEditor = ({enums, powerOptions}) => {
    const [regionsOptions, setRegionsOptions] = useState([]);
    const [valvesOptions, setValvesOptions] = useState([]);
    const [metersOptions, setMetersOptions] = useState([]);
    const {getAll} = useHardwareCatalog();
    const [isLoading, setIsLoading] = useState(true);

    const ViewPredefinedInstallationDialog = (columns, viewRow, setViewRow) => {
        const components = [];
        for (let i = 0; i < columns.length; i += 2) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(2)].map((_, j) => {
                        const column = columns[i + j];
                        return column && (
                            <Grid item xs={12} sm={6} key={column.accessorKey}>
                                <TextField
                                    label={column.header}
                                    defaultValue={renderColumnValue(column, viewRow, enums)}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    variant="standard"
                                    fullWidth
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog maxWidth="md" open={true}>
                <DialogTitle variant="h4">View Details</DialogTitle>
                <DialogContent>
                    {components}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const PredefinedInstallationDialog = (table, row, internalEditComponents, validationErrors, setValidationErrors, dialogType) => {
        const components = [];
        for (let i = 1; i < internalEditComponents.length; i += 2) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(2)].map((_, j) => {
                        const component = internalEditComponents[i + j];
                        return component && (
                            <Grid item xs={12} sm={6} key={`item-${i}-${j}`}>
                                {component}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }
        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={true}
            >
                <DialogTitle variant="h4">{dialogType === "edit" ? "Edit Pre Defined Installation" : "Create a new Pre Defined Installation"}</DialogTitle>
                <DialogContent
                    sx={{
                        overflowY: 'auto',
                    }}
                >
                    {components}
                </DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row}/>
                </DialogActions>
            </Dialog>
        );
    }

    const loadOptions = async () => {
        const valvesPromise = getAll("valves");
        const metersPromise = getAll("meters");
        const regionsPromise = getAll("regions");
        return Promise.all([valvesPromise, metersPromise, regionsPromise]);
    }


    useEffect(() => {
        loadOptions()
            .then(([valves, meters, regions]) => {
                setValvesOptions(valves.data.content?.map(valve => ({label: valve.name, value: valve})));
                setMetersOptions(meters.data.content?.map(meter => ({label: meter.name, value: meter})));
                setRegionsOptions(regions.data.content?.map(region => ({label: region.name, value: region})));
                setIsLoading(false);
            }).catch(error => {
            setIsLoading(false);
            console.error(error);

        });
    }, []);

    const createPredefinedInstallationColumns = (validationErrors, setValidationErrors) => {
        return [
            {
                accessorKey: 'id',
                header: 'Id',
                enableEditing: false,
                muiEditTextFieldProps: {
                    type: 'number',
                    hidden: true,
                },
            },
            {
                accessorKey: 'name',
                header: 'Name',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                },
            },
            {
                accessorKey: 'label',
                header: 'Label',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.label,
                    helperText: validationErrors?.label,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            label: undefined,
                        }),
                },
            },
            {
                accessorKey: 'aliases',
                header: 'Aliases',
                muiEditTextFieldProps: {
                    type: 'text',
                },
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    if (!value || value === '') {
                        return null;
                    }
                    return value.map((alias, index, array) => {
                        const separator = index < array.length - 1 ? ', ' : '';
                        return alias + separator;
                    });
                },
                Edit: ({row, column, cell}) => ArrayTextFieldEditor(
                    row,
                    column,
                    cell.getValue(),
                    false,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'region',
                header: 'Region',
                filterVariant: 'multi-select',
                filterSelectOptions: regionsOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    regionsOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'supplyMeter',
                header: 'Supply Meter',
                filterVariant: 'multi-select',
                filterSelectOptions: metersOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    metersOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'supplyValve',
                header: 'Supply Valve',
                filterVariant: 'multi-select',
                filterSelectOptions: valvesOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    valvesOptions,
                    false,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'returnMeter',
                header: 'Return Meter',
                filterVariant: 'multi-select',
                filterSelectOptions: metersOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    metersOptions,
                    false,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'returnValve',
                header: 'Return Valve',
                filterVariant: 'multi-select',
                filterSelectOptions: valvesOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    valvesOptions,
                    false,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'powerSupply',
                header: 'Power Supply',
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                editVariant: 'select',
                filterVariant: 'multi-select',
                filterSelectOptions: powerOptions.map(option => option.label),
                editSelectOptions: powerOptions,
                muiEditTextFieldProps: {
                    type: 'multi-select',
                    required: true,
                    error: !!validationErrors?.powerSupply,
                    helperText: validationErrors?.powerSupply,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            powerSupply: '',
                        }),
                },
            },

        ]
    }

    const predefinedInstallationValidationSchema = yup.object().shape({
        name: yup.string().required('Name is required').max(255, 'Name must be no longer than 255 characters'),
        aliases: yup.array()
            .of(
                yup.string()
                    .trim()
                    .max(255, 'Each alias must be no longer than 255 characters')
            )
            .optional()
            .nullable()
            .test('log-aliases', 'Logging aliases', function (value) {
                console.log('Aliases being validated:', value);
                return true;
            }),

        label: yup.string().required('Label is required').max(500, 'Label must be no longer than 500 characters'),
        region: yup.object().typeError('Region is required').required('Region is required'),
        supplyMeter: yup.object().typeError('Supply Meter is required').required('Supply Meter is required'),
        powerSupply: yup.string().required('Power Supply is required'),
    });

    return isLoading ? <LinearProgress/> : <CatalogEntity entityType={'predefined-installations'}
                                                          entityName={'Predefined Installation'}
                                                          createColumns={createPredefinedInstallationColumns}
                                                          validationSchema={predefinedInstallationValidationSchema}
                                                          customEditDialog={PredefinedInstallationDialog}
                                                          customCreateDialog={PredefinedInstallationDialog}
                                                          customViewDialog={ViewPredefinedInstallationDialog}
                                                          enums={enums}

    />
}

export default PredefinedInstallationEditor;
